import { CheckIcon, DropdownListOption } from '@viveaesthetics/shared'
import {
  convertEnumType,
  parseDateTimeStringInTimeZone
} from '@viveaesthetics/elemy-shared-core'
import { uniq } from 'lodash'

import { InNetworkWithPayers, Provider } from '@/hooks/useGetProviders'
import { ProviderTimeSlot } from '@/hooks/provider/useGetProvidersAvailableWindowByDay'
import { DAY_MAP, TIME_SLOT_START_END_MAP } from '@/constants/timeSlots'

import {
  InsurancesEnum,
  ProviderFilterTypes,
  StatesEnum,
  TimePeriodEnum
} from '../types/providers'

import { getNextAvailableSlotSinceDate } from './timeSlots'

/**
 * Get current origin from url.
 *
 * @returns {string}
 *   origin or empty string.
 */
export const getOrigin = () => {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin
  }
  return ''
}

export const getProviderFilterOptions = <T extends string>(
  options: Array<DropdownListOption<T>>,
  value?: string | string[],
  withCheckIcon = true
): Array<DropdownListOption<T>> =>
  options.map(option => ({
    ...option,
    rightActionIcon:
      withCheckIcon && option.value === value ? (
        <CheckIcon $color="iconSecondary" />
      ) : undefined
  }))

export const getProviderFilterOptionFromValue = <T extends string>(
  options: Array<DropdownListOption<T>>,
  value?: string | string[]
) => {
  if (!value) return undefined

  return Array.isArray(value)
    ? options.filter(option => value.includes(option.value))
    : options.filter(option => option.value === value)
}

export const getOptionsFromFiltersValues = <T extends string>({
  filterValues,
  translationMap,
  sort
}: {
  filterValues: T[]
  translationMap?: Record<string, string>
  sort?: { desc: boolean }
}): Array<DropdownListOption<T>> => {
  const options = filterValues.map(option => ({
    id: option,
    title: translationMap?.[option] || option,
    value: option
  }))

  if (typeof sort?.desc !== 'undefined') {
    options.sort((a, b) => {
      if (sort.desc) {
        return b.title.localeCompare(a.title)
      } else {
        return a.title.localeCompare(b.title)
      }
    })
  }
  return options
}

export const getFilteredProviders = ({
  providers,
  slotsForProviders,
  activeFilters,
  isInsuranceImprovementsEnabled
}: {
  providers: Provider[]
  slotsForProviders?: Record<string, ProviderTimeSlot[]>
  activeFilters: ProviderFilterTypes
  isInsuranceImprovementsEnabled?: boolean
}) => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

  return providers.filter(provider => {
    const state = activeFilters.state
    const insurance = activeFilters.insurance
      ? convertEnumType(activeFilters.insurance, InsurancesEnum)
      : InsurancesEnum.CASH
    let concerns: string[] = []
    if (activeFilters.concern) {
      concerns = Array.isArray(activeFilters.concern)
        ? activeFilters.concern
        : [activeFilters.concern]
    }
    const typeOfCare = activeFilters.typeOfCare
    const genders = activeFilters.gender
    const identities = activeFilters.identity
    const day = activeFilters.day
    const time = activeFilters.time

    let standardFiltersResult =
      (!state || provider.operatesInProvinces.includes(state)) &&
      (!concerns.length ||
        concerns.some(concern => provider.specialties.includes(concern))) &&
      (!typeOfCare || provider.typesOfCare.some(item => typeOfCare === item)) &&
      (!genders?.length ||
        genders.some(gender => provider.gender === gender)) &&
      (!identities?.length ||
        identities.some(identity => provider.identifiesAs.includes(identity)))

    if (insurance && !isInsuranceImprovementsEnabled) {
      if (insurance === InsurancesEnum.CASH) {
        standardFiltersResult &&= provider.acceptsCashPay
      } else {
        standardFiltersResult &&= provider.inNetworkWithPayers.some(
          ({ province, payerName }) =>
            province === state && payerName === insurance
        )
      }
    }
    if (day?.length || time?.length) {
      standardFiltersResult &&= !!slotsForProviders?.[provider.id]?.some(
        slot => {
          const startDate = parseDateTimeStringInTimeZone(
            slot.startTime,
            timeZone
          )
          const dayIndex = startDate.getDay()
          const timeSlotHour = startDate.getHours()

          const days = Array.isArray(day) ? day : [day]
          const daysIndexes = days.map(day =>
            DAY_MAP.findIndex(dayName => dayName === day)
          )

          let dayMatch = true
          let timeMatch = true
          if (day?.length) {
            dayMatch = daysIndexes.includes(dayIndex)
          }
          if (time?.length) {
            timeMatch = time.some(timeSlot => {
              const timeEnum = convertEnumType(timeSlot, TimePeriodEnum)
              return (
                timeSlotHour >= TIME_SLOT_START_END_MAP[timeEnum].start &&
                timeSlotHour < TIME_SLOT_START_END_MAP[timeEnum].end
              )
            })
          }

          return dayMatch && timeMatch
        }
      )
    } else {
      standardFiltersResult &&= !!slotsForProviders?.[provider.id]?.length
    }

    return standardFiltersResult
  })
}

export const getUniqueInsurances = (
  inNetworkWithPayers: InNetworkWithPayers[],
  state: StatesEnum
) =>
  uniq(inNetworkWithPayers.filter(({ province }) => province === state)).map(
    insurance => insurance
  )

// TODO: remove with pedp-3858-booking-insurance-id
export const getUniqueInsuranceNames = (
  inNetworkWithPayers: InNetworkWithPayers[],
  state: StatesEnum
) =>
  uniq(inNetworkWithPayers.filter(({ province }) => province === state)).map(
    ({ payerName }) => payerName
  )

const PERSISTED_GET_PARAMETERS = [
  'insurance',
  'insuranceId',
  'cash_payment',
  'state',
  'time_slot',
  'therapist_id',
  'typeOfCare'
]

export const THERAPISTS_PAGE_PERSISTED_PARAMS = [
  'insurance',
  'insuranceId',
  'state',
  'time_slot',
  'therapist_id',
  'typeOfCare',
  'concern',
  'gender',
  'identity',
  'day',
  'time'
]

export const getUrlWithPersistedParams = (
  url: string,
  paramsToPersist: string[] = PERSISTED_GET_PARAMETERS
): string => {
  const search = typeof window !== 'undefined' ? window.location.search : ''
  const urlParams = new URLSearchParams(search)
  const params = new URLSearchParams()

  urlParams.forEach((value, key) => {
    if (
      paramsToPersist.includes(key) &&
      !url.includes(value) &&
      !url.includes(key)
    ) {
      params.append(key, value)
    }
  })

  const persistedParamsString = params.toString()

  if (persistedParamsString) {
    return `${url}${url.includes('?') ? '&' : '?'}${persistedParamsString}`
  }

  return url
}

export const orderProvidersByNearestAvailability = (
  providers: Provider[],
  slotsForProviders: Record<string, ProviderTimeSlot[]>,
  sinceDateTime: Date
) => {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
  return providers.sort((providerA, providerB) => {
    const providerANextAvailableSlot = getNextAvailableSlotSinceDate(
      slotsForProviders[providerA.id] || [],
      sinceDateTime
    )
    const providerBNextAvailableSlot = getNextAvailableSlotSinceDate(
      slotsForProviders[providerB.id] || [],
      sinceDateTime
    )

    if (!providerANextAvailableSlot && !providerBNextAvailableSlot) return 0
    if (!providerANextAvailableSlot) return 1
    if (!providerBNextAvailableSlot) return -1

    const providerAStartDate = parseDateTimeStringInTimeZone(
      providerANextAvailableSlot.startTime,
      timeZone
    )
    const providerBStartDate = parseDateTimeStringInTimeZone(
      providerBNextAvailableSlot.startTime,
      timeZone
    )

    return providerAStartDate.getTime() - providerBStartDate.getTime()
  })
}

export const handleFilterChange = ({
  key,
  value,
  activeFiltersExtended,
  setPageIndex,
  scrollableContainer,
  changeUrlParams,
  changeSlugParams
}: {
  key: keyof ProviderFilterTypes
  value?: string | string[]
  activeFiltersExtended: ProviderFilterTypes
  setPageIndex: (index: number) => void
  scrollableContainer: HTMLElement | null
  changeUrlParams: (
    keys: Partial<Record<keyof ProviderFilterTypes, string | string[]>>
  ) => void
  changeSlugParams?: (
    keys: Partial<Record<keyof ProviderFilterTypes, string | string[]>>
  ) => void
}) => {
  if (activeFiltersExtended[key] === value) return

  const updatedKeys: Partial<
    Record<keyof ProviderFilterTypes, string | string[]>
  > = {}
  if (key !== 'pageIndex') {
    setPageIndex(0)
    updatedKeys.pageIndex = ''
  }
  scrollableContainer?.scrollTo({
    top: 0,
    behavior: 'smooth'
  })

  if (changeSlugParams && (key === 'state' || key === 'city')) {
    changeSlugParams({ [key]: value })
  } else {
    updatedKeys[key] = value
    changeUrlParams(updatedKeys)
  }
}
